import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts';
import { useAuthStore } from './store';
import { clearPersistedQueryCache } from './offline/queryPersistence';

// Vite exposes env vars prefixed with VITE_ via import.meta.env
// Aspire sets VITE_KEYCLOAK_URL to the actual dynamic endpoint
export const KEYCLOAK_URL = (import.meta as any).env?.VITE_KEYCLOAK_URL || 'http://localhost:8080';
export const REALM = (import.meta as any).env?.VITE_KEYCLOAK_REALM || 'sport-school';
/**
 * True inside the mobile app's WebView. `window.ReactNativeWebView` is the native bridge, present before any page
 * script runs; the injected `window.isMobileApp` flag may arrive after this module on Android (e.g. app shell served
 * by the service worker), which made login start and finish with different client ids ("client_id mismatch").
 */
export function isRunningInMobileApp(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as { isMobileApp?: boolean; ReactNativeWebView?: unknown };
  return !!w.isMobileApp || !!w.ReactNativeWebView;
}

const isMobileApp = isRunningInMobileApp();
export const CLIENT_ID = isMobileApp ? 'mobile-app' : 'web-spa';
const REDIRECT_URI = isMobileApp
  ? 'badmintomania://auth/callback'
  : typeof window !== 'undefined'
    ? `${window.location.origin}/auth/callback`
    : '';

export const userManager = new UserManager({
  authority: `${KEYCLOAK_URL}/realms/${REALM}`,
  client_id: CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  // Keycloak rejects a bare 'badmintomania://' (nothing after the scheme) as a post-logout redirect URI
  post_logout_redirect_uri: isMobileApp
    ? 'badmintomania://auth/logout'
    : typeof window !== 'undefined'
      ? `${window.location.origin}/`
      : '',
  response_type: 'code',
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  // metadataSeed is used before the discovery document is fetched;
  // oidc-client-ts will call .well-known/openid-configuration automatically
  metadataSeed: {
    issuer: `${KEYCLOAK_URL}/realms/${REALM}`,
  },
});

/** `navigator.onLine === false` — the device has no network at all. */
export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * True when a silent renew failed because KeyCloak could not be reached (device offline / fetch failed),
 * not because KeyCloak rejected the session. In that case the stored user must be kept: the app keeps working
 * from the offline cache and the token is renewed once the connection is back.
 */
export function isAuthNetworkFailure(error: unknown): boolean {
  return isBrowserOffline() || error instanceof TypeError;
}

let _silentRenewPromise: Promise<any> | null = null;
let _signinRedirectPromise: Promise<void> | null = null;

// A page restored from the back/forward cache must be able to start a new login.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) _signinRedirectPromise = null;
  });
}

/**
 * Redirects to the KeyCloak login page at most once per page load. Every authorization
 * request creates its own KeyCloak authentication session; with parallel requests the
 * browser ends up posting the login form of one session with the cookie of another and
 * KeyCloak answers "You took too long to log in" (loginTimeout).
 */
export function login(): Promise<void> {
  if (!_signinRedirectPromise) {
    _signinRedirectPromise = userManager.signinRedirect().catch((error) => {
      _signinRedirectPromise = null;
      throw error;
    });
  }
  return _signinRedirectPromise;
}

/** Error code the API returns (HTTP 403) when an endpoint needs a fresh interactive login. */
export const RECENT_LOGIN_REQUIRED_CODE = 'recent_login_required';

const REAUTH_RETURN_URL_KEY = 'auth_reauth_return_url';

export function isRecentLoginRequiredError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  return response?.status === 403 && response.data?.code === RECENT_LOGIN_REQUIRED_CODE;
}

/**
 * Step-up for sensitive pages: forces KeyCloak to ask for credentials again even with a live SSO session
 * (`prompt=login`), so the new token carries a fresh `auth_time`. Returns to `returnUrl` afterwards.
 */
export function reauthenticate(returnUrl: string): Promise<void> {
  sessionStorage.setItem(REAUTH_RETURN_URL_KEY, returnUrl);
  return userManager.signinRedirect({ prompt: 'login' });
}

/** Returns (and clears) the page that started a step-up login, if any. */
export function consumeReauthReturnUrl(): string | null {
  const url = sessionStorage.getItem(REAUTH_RETURN_URL_KEY);
  sessionStorage.removeItem(REAUTH_RETURN_URL_KEY);
  return url;
}

/**
 * Detaches this device's push token from the signed-in user so they stop receiving
 * notifications here after logout. Best-effort: logout must not fail because of it.
 */
async function unregisterDevicePushToken() {
  const pushToken = typeof window !== 'undefined'
    ? (window as { __EXPO_PUSH_TOKEN__?: string }).__EXPO_PUSH_TOKEN__
    : undefined;
  if (!pushToken || useAuthStore.getState().impersonationToken) return;

  try {
    const { api } = await import('./api');
    await api.delete(`/notifications/push-tokens/${encodeURIComponent(pushToken)}`, { skipAuthRedirect: true });
  } catch {
    // Ignore — the token is re-assigned on the next login on this device anyway.
  }
}

export async function logout() {
  await unregisterDevicePushToken();
  // Read the id token before removing the user — without id_token_hint KeyCloak shows a logout confirmation page.
  const idToken = (await userManager.getUser())?.id_token;
  useAuthStore.getState().logout();
  await userManager.removeUser();
  // The persisted query cache belongs to this user. Unsent outbox entries are kept (the UI warns before logout).
  await clearPersistedQueryCache();

  try {
    await userManager.signoutRedirect({ id_token_hint: idToken });
  } catch {
    window.location.href = '/';
  }
}

let _callbackInFlight = false;

export async function handleAuthCallback(): Promise<boolean> {
  if (_callbackInFlight) return false;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('code') && !params.has('state')) return false;

  _callbackInFlight = true;
  try {
    await userManager.signinRedirectCallback();
    window.history.replaceState({}, document.title, window.location.pathname);
    return true;
  } finally {
    _callbackInFlight = false;
  }
}

export async function getAccessToken(): Promise<string | null> {
  const storeState = useAuthStore.getState();
  if (storeState.impersonationToken) {
    return storeState.impersonationToken;
  }

  let user = await userManager.getUser();
  if (user && (user.expired || (user.expires_in !== undefined && user.expires_in <= 15))) {
    if (!_silentRenewPromise) {
      _silentRenewPromise = userManager.signinSilent().catch(() => null).finally(() => {
        _silentRenewPromise = null;
      });
    }
    const renewed = await _silentRenewPromise;
    if (renewed?.access_token) {
      return renewed.access_token;
    }
  }

  return user?.access_token ?? null;
}

export async function initAuth() {
  try {
    let user = await userManager.getUser();
    if (user && user.expired) {
      // Offline: keep the stored (expired) user so the app renders from the offline cache; the token is renewed
      // when the connection is back (API interceptor / sync engine).
      if (isBrowserOffline()) return user;
      try {
        user = await userManager.signinSilent();
      } catch (error) {
        // KeyCloak unreachable — keep the user, never force a login redirect that cannot load.
        if (isAuthNetworkFailure(error)) return user;
        // Silent renew rejected — clear the stale user to force a fresh login
        await userManager.removeUser();
        return null;
      }
    }
    return user;
  } catch {
    return null;
  }
}

export function parseJwt(token: string | null | undefined): any {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function isMasterAdmin(token: string | null | undefined): boolean {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload) return false;

  // Check 'roles' claim
  if (Array.isArray(payload.roles) && payload.roles.includes('master-admin')) {
    return true;
  }
  if (typeof payload.roles === 'string' && payload.roles === 'master-admin') {
    return true;
  }

  // Check 'realm_access.roles' claim (standard Keycloak)
  if (
    payload.realm_access &&
    Array.isArray(payload.realm_access.roles) &&
    payload.realm_access.roles.includes('master-admin')
  ) {
    return true;
  }

  return false;
}

export interface TokenExchangeResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
  scope?: string;
}

export async function setAuthTokens(tokens: TokenExchangeResponse): Promise<User> {
  const payload = parseJwt(tokens.id_token || tokens.access_token);
  const user = new User({
    id_token: tokens.id_token,
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    token_type: tokens.token_type || 'Bearer',
    scope: tokens.scope || 'openid profile email',
    profile: (payload || {}) as any,
    expires_at: tokens.expires_in ? Math.floor(Date.now() / 1000) + tokens.expires_in : undefined,
  });

  await userManager.storeUser(user);
  useAuthStore.getState().setToken(tokens.access_token);
  return user;
}

export async function exchangeGoogleTokenWithKeycloak(googleIdToken: string): Promise<User> {
  const tokenEndpoint = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`;

  const body = new URLSearchParams({
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    client_id: CLIENT_ID,
    subject_token: googleIdToken,
    subject_token_type: 'urn:ietf:params:oauth:token-type:id_token',
    subject_issuer: 'google',
    scope: 'openid profile email',
  });

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Keycloak token exchange failed:', response.status, errorText);
    throw new Error(`Google login token exchange failed (${response.status}): ${errorText}`);
  }

  const tokens = (await response.json()) as TokenExchangeResponse;
  return await setAuthTokens(tokens);
}


